import { useCallback, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  ANDROID_STORE_URL,
  IOS_STORE_URL,
} from '../../constant/appLinks';
import { getMobileOS } from '../../utils/platform';
import {
  openAndroidApp,
  openAndroidAppCustomScheme,
  openIOSApp,
} from '../../utils/openNativeApp';
import Logo from '../../assets/icons/logo.png';
import './_deepLinkRedirect.scss';

const IOS_STORE_FALLBACK_DELAY = 2500;
const ANDROID_CUSTOM_SCHEME_RETRY_DELAY = 800;

const appendUtmSource = (storeUrl: string): string => {
  if (storeUrl.includes('utm_source=')) {
    return storeUrl;
  }
  const separator = storeUrl.includes('?') ? '&' : '?';
  return `${storeUrl}${separator}utm_source=deep_link`;
};

const DeepLinkRedirect = () => {
  const location = useLocation();
  const os = getMobileOS();
  const pathname = location.pathname;
  const storeUrl =
    os === 'ios' ? IOS_STORE_URL : ANDROID_STORE_URL;
  const storeUrlWithUtm = appendUtmSource(storeUrl);

  const handleOpenApp = useCallback(() => {
    if (os === 'android') {
      openAndroidApp(pathname, storeUrlWithUtm, { includeStoreFallback: true });
    } else if (os === 'ios') {
      openIOSApp(pathname, location.search, location.hash);
    }
  }, [os, pathname, location.search, location.hash, storeUrlWithUtm]);

  useEffect(() => {
    if (os === 'other') {
      return;
    }

    let didHide = false;

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        didHide = true;
      }
    };
    const onPageHide = () => {
      didHide = true;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);

    if (os === 'android') {
      // HTTPS intent matches React Navigation prefixes (https://sporti.app/...).
      // No Play Store in the intent on auto-open — wrong format was sending users
      // to the store even when com.sporti.app was installed.
      openAndroidApp(pathname, storeUrlWithUtm, { includeStoreFallback: false });

      const customSchemeRetryId = window.setTimeout(() => {
        if (!didHide && document.visibilityState === 'visible') {
          openAndroidAppCustomScheme(pathname);
        }
      }, ANDROID_CUSTOM_SCHEME_RETRY_DELAY);

      return () => {
        window.clearTimeout(customSchemeRetryId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      };
    } else {
      openIOSApp(pathname, location.search, location.hash);

      const timeoutId = window.setTimeout(() => {
        if (!didHide && document.visibilityState === 'visible') {
          window.location.replace(storeUrlWithUtm);
        }
      }, IOS_STORE_FALLBACK_DELAY);

      return () => {
        window.clearTimeout(timeoutId);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pagehide', onPageHide);
      };
    }
  }, [location, os, pathname, location.search, location.hash, storeUrlWithUtm]);

  if (os === 'other') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="deep-link-redirect">
      <img src={Logo} alt="Sporti" className="deep-link-redirect__logo" />
      <div className="deep-link-redirect__spinner" />
      <p className="deep-link-redirect__text">Opening Sporti...</p>
      <div className="deep-link-redirect__actions">
        <button
          type="button"
          className="deep-link-redirect__btn deep-link-redirect__btn--primary"
          onClick={handleOpenApp}
        >
          Open in Sporti
        </button>
        <a
          className="deep-link-redirect__btn deep-link-redirect__btn--secondary"
          href={storeUrlWithUtm}
        >
          Get the app
        </a>
      </div>
    </div>
  );
};

export default DeepLinkRedirect;
